import { OstiumSubgraphError, OstiumSubgraphErrorCode } from '../errors.js';
import type { GetOrdersParams, Order } from '../types.js';

export const TRADE_ID_BATCH_SIZE = 500;

export interface OrderQueryFlags {
  hasOrderIds: boolean;
  hasInitiatedTxHashes: boolean;
  hasTrader: boolean;
  hasBuilder: boolean;
  hasIsPending: boolean;
  hasIsCancelled: boolean;
  hasPairIds: boolean;
  hasTradeIds: boolean;
  hasExcludeIds: boolean;
  hasExecutedStart: boolean;
  hasExecutedEnd: boolean;
  isCancelled: boolean | undefined;
}

export function validateOrderFilterParams(params: GetOrdersParams): void {
  const hasOrderIds = !!(params.orderIds && params.orderIds.length > 0);
  const hasInitiatedTxHashes = !!(params.initiatedTxHashes && params.initiatedTxHashes.length > 0);
  if (hasOrderIds && hasInitiatedTxHashes) {
    throw new OstiumSubgraphError(
      'getOrders: pass either orderIds or initiatedTxHashes, not both',
      OstiumSubgraphErrorCode.INVALID_PARAMS,
    );
  }

  if (
    params.isCanceled !== undefined &&
    params.isCancelled !== undefined &&
    params.isCanceled !== params.isCancelled
  ) {
    throw new OstiumSubgraphError(
      'getOrders: pass either isCanceled or isCancelled, not conflicting values for both',
      OstiumSubgraphErrorCode.INVALID_PARAMS,
    );
  }

  if (params.start !== undefined && params.end !== undefined && params.start >= params.end) {
    throw new OstiumSubgraphError(
      'getOrders: start must be less than end',
      OstiumSubgraphErrorCode.INVALID_PARAMS,
    );
  }
}

export function resolveOrderQueryFlags(
  params: GetOrdersParams,
  extra?: { tradeIds?: string[]; excludeIds?: string[] },
): OrderQueryFlags {
  const isCancelled = params.isCanceled ?? params.isCancelled;
  return {
    hasOrderIds: !!(params.orderIds && params.orderIds.length > 0),
    hasInitiatedTxHashes: !!(params.initiatedTxHashes && params.initiatedTxHashes.length > 0),
    hasTrader: params.user !== undefined && params.user !== 'ALL',
    hasBuilder: !!params.builder,
    hasIsPending: params.isPending !== undefined,
    hasIsCancelled: isCancelled !== undefined,
    hasPairIds: !!(params.pairIds && params.pairIds.length > 0),
    hasTradeIds: !!(extra?.tradeIds && extra.tradeIds.length > 0),
    hasExcludeIds: !!(extra?.excludeIds && extra.excludeIds.length > 0),
    hasExecutedStart: params.start !== undefined,
    hasExecutedEnd: params.end !== undefined,
    isCancelled,
  };
}

export function mergeOrders(...groups: Order[][]): Order[] {
  const byOid = new Map<string, Order>();
  for (const group of groups) {
    for (const order of group) {
      byOid.set(order.oid, order);
    }
  }
  return [...byOid.values()].sort((a, b) => b.initiatedTime - a.initiatedTime);
}

export function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}
